/**
 * Purchase order line pricing helpers.
 *
 * Semantics:
 * - unit_price on PO lines = Selling Price (trade price, admin-editable)
 * - original_unit_price / mrp = MRP snapshot at order time
 */

import {
  getLineDivisionSlug,
  normalizeModifyAcceptQty,
  validateModifyAcceptQuantity,
} from "./quantityValidation";

export { sellingPriceExceedsMrp } from "./sellingPriceMrp";

/**
 * Current Selling Price / TP for a PO line.
 * Prefer live `unit_price` (updated by modify-accept) over frozen pricing snapshot.
 */
export function getLineSellingPriceTp(line) {
  if (line?.unit_price != null && line.unit_price !== "") {
    const n = Number(line.unit_price);
    if (Number.isFinite(n)) return n;
  }
  const fromAlias =
    line?.selling_price_tp ?? line?.pricing?.selling_price_tp;
  const n = Number(fromAlias ?? 0);
  return Number.isFinite(n) ? n : 0;
}

/**
 * Selling Price / TP at line create (before admin modify-accept).
 * Falls back to current TP when never modified / not stored.
 */
export function getLineOriginalSellingPriceTp(line, order = null) {
  const fromField = line?.original_selling_price_tp;
  if (fromField != null && fromField !== "") {
    const n = Number(fromField);
    if (Number.isFinite(n)) return n;
  }
  const fromMeta = line?.meta?.original_selling_price_tp;
  if (fromMeta != null && fromMeta !== "") {
    const n = Number(fromMeta);
    if (Number.isFinite(n)) return n;
  }
  const lineId = getModifyAcceptLineId(line);
  if (lineId != null && order) {
    const fromHistory = findEarliestOldSellingPriceFromHistory(order, lineId);
    if (fromHistory != null) return fromHistory;
    const fromAudit = findEarliestOldSellingPriceFromAudit(order, lineId);
    if (fromAudit != null) return fromAudit;
  }
  return getLineSellingPriceTp(line);
}

export function lineSellingPriceWasModified(line, order = null) {
  return !unitPricesEqual(
    getLineOriginalSellingPriceTp(line, order),
    getLineSellingPriceTp(line),
  );
}

function findEarliestOldSellingPriceFromHistory(order, lineId) {
  const history = Array.isArray(order?.meta?.history) ? order.meta.history : [];
  let earliest = null;
  for (const entry of history) {
    const changes = Array.isArray(entry?.changes) ? entry.changes : [];
    for (const change of changes) {
      const id = change?.order_line_item_id ?? change?.orderLineItemId;
      if (Number(id) !== Number(lineId)) continue;
      if (change?.old_unit_price == null) continue;
      const n = Number(change.old_unit_price);
      if (!Number.isFinite(n)) continue;
      earliest = n;
      break;
    }
    if (earliest != null) break;
  }
  return earliest;
}

function findEarliestOldSellingPriceFromAudit(order, lineId) {
  const audit = Array.isArray(order?.price_audit)
    ? order.price_audit
    : Array.isArray(order?.meta?.price_audit)
      ? order.meta.price_audit
      : [];
  for (const event of audit) {
    if (event?.event !== "SELLING_PRICE_TP_UPDATED") continue;
    const id = event?.order_line_item_id ?? event?.orderLineItemId;
    if (Number(id) !== Number(lineId)) continue;
    const n = Number(event.old_selling_price_tp ?? event.oldSellingPriceTp);
    if (Number.isFinite(n)) return n;
  }
  return null;
}

export function getModifyAcceptLineId(line) {
  return line?.id ?? line?.order_line_item_id ?? null;
}

export function quantitiesEqual(a, b) {
  return Math.round(Number(a) * 100) === Math.round(Number(b) * 100);
}

export function parseModifyAcceptQtyInput(value, options = {}) {
  return validateModifyAcceptQuantity(value, options.divisionSlug, {
    allowZero: options.allowZero ?? false,
  });
}

export function clampModifyAcceptQty(value, divisionSlug, options = {}) {
  return normalizeModifyAcceptQty(value, divisionSlug, options);
}

export function clampModifyAcceptPendingQty(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return 1;
  return Math.max(1, Math.floor(n));
}

/** Resolve editable unit price for preview/submit (state overrides line snapshot). */
export function resolveLineUnitPriceForPreview(line, unitPrices, lineId) {
  if (lineId == null) return getLineSellingPriceTp(line);
  const raw = unitPrices[lineId];
  if (raw !== undefined && raw !== null && raw !== "") {
    const n = Number(raw);
    return Number.isFinite(n) ? n : 0;
  }
  return getLineSellingPriceTp(line);
}

/** @deprecated use getLineSellingPriceTp */
export function getLineUnitPrice(line) {
  return getLineSellingPriceTp(line);
}

export function getLineMrp(line) {
  if (line?.mrp != null && line.mrp !== "") {
    const n = Number(line.mrp);
    if (Number.isFinite(n)) return n;
  }
  if (line?.original_unit_price != null && line.original_unit_price !== "") {
    const n = Number(line.original_unit_price);
    if (Number.isFinite(n)) return n;
  }
  return null;
}

export function formatLineMrp(line) {
  const mrp = getLineMrp(line);
  return mrp == null ? "—" : mrp;
}

export function parseSellingPriceTpInput(value) {
  if (value === "" || value === null || value === undefined) {
    return { ok: false, error: "Selling Price is required" };
  }
  const n = Number(value);
  if (!Number.isFinite(n)) {
    return { ok: false, error: "Selling Price must be a valid number" };
  }
  if (!(n > 0)) {
    return { ok: false, error: "Selling Price must be greater than 0" };
  }
  return { ok: true, value: n };
}

/** @deprecated use parseSellingPriceTpInput */
export function parseUnitPriceInput(value) {
  return parseSellingPriceTpInput(value);
}

export function unitPricesEqual(a, b) {
  return Math.round(Number(a) * 100) === Math.round(Number(b) * 100);
}

export function computePreviewLineTotal(qty, unitPrice) {
  const q = Number(qty) || 0;
  const u = Number(unitPrice) || 0;
  return Math.round(q * u * 100) / 100;
}

/**
 * Preview subtotal for Modify & Accept (existing lines + pending adds).
 */
export function computeModifyAcceptPreviewTotal(
  lineItems,
  quantities,
  unitPrices,
  pendingAdds = [],
) {
  let sum = 0;
  for (const li of lineItems || []) {
    const id = getModifyAcceptLineId(li);
    if (!id) continue;
    const divisionSlug = getLineDivisionSlug(li);
    const qty = normalizeModifyAcceptQty(quantities[id], divisionSlug, {
      allowZero: true,
    });
    if (qty === 0) continue;
    const unit = resolveLineUnitPriceForPreview(li, unitPrices, id);
    sum += computePreviewLineTotal(qty, unit);
  }
  for (const row of pendingAdds || []) {
    const qty = clampModifyAcceptPendingQty(row.quantity);
    sum += computePreviewLineTotal(qty, row.unit_price);
  }
  return Math.round(sum * 100) / 100;
}

/**
 * Build partial modify-accept payload entries (mirrors ModifyAcceptOrderModal).
 */
export function buildModifyAcceptAdjustments(lineItems, quantities, unitPrices) {
  const adjustments = [];
  for (const li of lineItems || []) {
    const id = getModifyAcceptLineId(li);
    if (!id) continue;
    const divisionSlug = getLineDivisionSlug(li);
    const currentQty = normalizeModifyAcceptQty(li.quantity ?? 0, divisionSlug, {
      allowZero: true,
    });
    const newQty = normalizeModifyAcceptQty(quantities[id], divisionSlug, {
      allowZero: true,
    });
    const currentUnit = getLineSellingPriceTp(li);
    const newUnit = resolveLineUnitPriceForPreview(li, unitPrices, id);
    const qtyChanged = !quantitiesEqual(newQty, currentQty);
    const priceChanged = !unitPricesEqual(newUnit, currentUnit);
    if (!qtyChanged && !priceChanged) continue;
    const entry = { orderLineItemId: Number(id) };
    if (qtyChanged) entry.new_quantity = newQty;
    if (priceChanged) entry.unit_price = newUnit;
    adjustments.push(entry);
  }
  return adjustments;
}

/**
 * Build newLineItems payload for Modify & Accept (products not on the order).
 * Pending rows with qty < 1 are omitted.
 * Includes catalog_unit_price when present so the backend can record Selling Price
 * changes vs the catalog baseline in order history / timeline.
 */
export function buildModifyAcceptNewLineItems(pendingAdds) {
  const items = [];
  for (const row of pendingAdds || []) {
    const productId = String(row.admin_product_id || "").trim();
    const qty = Math.floor(Number(row.quantity));
    const unitPrice = Number(row.unit_price);
    if (!productId || !Number.isInteger(qty) || qty < 1) continue;
    if (!Number.isFinite(unitPrice) || unitPrice < 0) continue;
    const catalogUnitPrice = Number(
      row.catalog_unit_price ?? row.current_unit_price,
    );
    const item = {
      admin_product_id: productId,
      quantity: qty,
      unit_price: unitPrice,
    };
    if (Number.isFinite(catalogUnitPrice) && catalogUnitPrice >= 0) {
      item.catalog_unit_price = catalogUnitPrice;
    }
    items.push(item);
  }
  return items;
}
